import axios from "axios";
import elliemaeApi from "./interceptors/elliemaeApi";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

/**
 * Calls the internal API to generate a filled PDF from field values.
 * 
 * @param {Array<Object>} updatedFields - List of field objects including PDFFieldName, value, type, etc.
 * @param {string} documentationType - Type of documentation ("Income" or "DSCR") to select the correct PDF template.
 * @returns {Promise<Blob>} - The filled PDF returned as a Blob for further processing (upload or display).
 */
export const generateFilledPDF = async (updatedFields, documentationType) => {
    const url = `${API_DOMAIN}/api/pdf/fill`;

    try {
        const response = await axios.post(url, {
            documentationType,
            fields: updatedFields
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'blob', // Important for binary data
        });

        if (response.status === 200) {
            return response.data; // Blob
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
    }
};

/**
 * Gets document ID if title exists
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} title
 * @returns {Promise<string|null>}
 */
export const getExistingDocumentId = async (accessToken, loanId, title) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanId}/documents?includeRemoved=true&requireActiveAttachments=false`;
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        const existingDoc = response.data.find(doc => doc.title === title);
        return existingDoc ? existingDoc.id : null;

    } catch (error) {
        console.error("Error fetching existing document ID:", error.message);
        throw error;
    }
};

/**
 * Creates a new document in Encompass
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} title
 * @returns {Promise<string>} new document ID
 */
export const createDocument = async (accessToken, loanId, title) => {
    if (!accessToken) return null;

    const url = `/encompass/v3/loans/${loanId}/documents?action=add&view=entity`;
    const payload = [
        {
            title: title,
            description: title
        }
    ];

    try {
        const response = await elliemaeApi.patch(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200 || response.status === 201) {
            return response.data[0].id;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error creating document:", error.message);
        throw error;
    }
};

/**
 * Gets the upload URL and attachment metadata
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} fileName
 * @param {number} fileSize
 * @returns {Promise<Object>} attachment upload info
 */
export const getAttachmentUploadUrl = async (accessToken, loanId, fileName, fileSize, contentType) => {
    if (!accessToken) return null;

    const url = `/encompass/v3/loans/${loanId}/attachmentUploadUrl`;

    const finalContentType = contentType && contentType.trim() !== ""
        ? contentType
        : "application/pdf";
    const payload = {
        file: {
            contentType: finalContentType,
            name: fileName,
            size: fileSize
        },
        title: fileName.replace(/\.[^/.]+$/, "")
    };

    try {
        const response = await elliemaeApi.post(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        return response.data;

    } catch (error) {
        console.error("Error getting upload URL:", error.message);
        throw error;
    }
};

/**
 * Uploads the PDF blob to the pre-signed upload URL
 * @param {string} uploadUrl
 * @param {string} authorizationHeader
 * @param {Blob} pdfBlob
 * @returns {Promise<void>}
 */
export const uploadAttachmentBlob = async (uploadUrl, authorizationHeader, pdfBlob, contentType) => {

    const finalContentType = contentType && contentType.trim() !== ""
        ? contentType
        : "application/pdf";
    try {
        await axios.put(uploadUrl, pdfBlob, {
            headers: {
                Authorization: authorizationHeader,
                "Content-Type": finalContentType
            }
        });
    } catch (error) {
        console.error("Error uploading attachment:", error.message);
        throw error;
    }
};

/**
 * Assigns uploaded attachment to the Encompass document
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} documentId
 * @param {string} attachmentId
 * @returns {Promise<void>}
 */
export const assignAttachmentToDocument = async (accessToken, loanId, documentId, attachmentId) => {
    const url = `/encompass/v3/loans/${loanId}/documents/${documentId}/attachments?action=add`;
    const payload = [
        {
            entityId: attachmentId,
            entityType: "attachment"
        }
    ];

    try {
        await elliemaeApi.patch(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });
    } catch (error) {
        console.error("Error assigning attachment to document:", error.message);
        throw error;
    }
};


/**
 * Fetches the latest active attachment ID for a given document title
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} documentTitle - e.g., "Approval Certificate", "Loan Estimate"
 * @returns {Promise<string|null>}
 */
export const getLatestDocumentAttachmentId = async (accessToken, loanId, documentTitle) => {
    const url = `/encompass/v3/loans/${loanId}/attachments?includeRemoved=false`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            }
        });

        const matchingAttachments = (response.data || [])
            .filter(doc => doc.title === documentTitle && doc.isActive)
            .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

        return matchingAttachments.length > 0 ? matchingAttachments[0].id : null;

    } catch (error) {
        console.error(`Error fetching attachment for "${documentTitle}":`, error.message);
        throw error;
    }
};


/**
 * Downloads the attachment blob by ID
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} attachmentId
 * @returns {Promise<Blob>}
 */
export const downloadAttachmentById = async (accessToken, loanId, attachmentId) => {
    const url = `/encompass/v3/loans/${loanId}/attachmentDownloadUrl`;
    const payload = {
        attachments: [attachmentId]
    };

    try {
        const response = await elliemaeApi.post(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        const file = response.data?.attachments?.[0];
        if (file?.url && file?.authorizationHeader) {
            const blobResponse = await axios.get(file.url, {
                headers: {
                    Authorization: file.authorizationHeader
                },
                responseType: 'blob'
            });

            return blobResponse.data;
        }

        throw new Error("Attachment download URL or header not found.");
    } catch (error) {
        console.error("Error downloading attachment:", error.message);
        throw error;
    }
};

/**
 * Checks if a document is assigned with a specific entity name and is active
 * @param {string} token - Bearer access token
 * @param {string} loanId - Encompass loan ID
 * @param {string} expectedEntityName - Expected value of assignedTo.entityName
 * @returns {Promise<boolean>} - True if such a document is found, otherwise false
 */
export const checkDocumentByAssignedEntity = async (token, loanId, expectedEntityName) => {
    const url = `/encompass/v3/loans/${loanId}/attachments?includeRemoved=false`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            }
        });

        const documents = response.data || [];

        return documents.some(doc =>
            doc.isActive &&
            doc.assignedTo?.entityType === 'Document' &&
            doc.assignedTo?.entityName === expectedEntityName
        );
    } catch (error) {
        console.error(`Error checking document by assigned entity:`, error.message);
        return false;
    }
};

/**
 * Fetches the latest Approval Certificate attachment IDs for the given loan.
 *
 * @param {string} authCode - The authorization code from Encompass guest session.
 * @param {string} loanId - The Encompass loan ID to look up the document for.
 * @returns {Promise<{ ConditionalAttachmentId: string | null, FinalAttachmentId: string | null }>} - Object with both attachment IDs.
 */
export const fetchLatestApprovalCertificate = async (authCode, loanId) => {
    const url = `${API_DOMAIN}/api/approvalcertificate/get-attachment-id`;
    const response = await axios.post(url, { authCode, loanId });
    return response.data;
};

/**
 * Fetch the latest approval certificate attachment ids/details for a loan.
 * Expected backend endpoint: POST {API_DOMAIN}/api/approvalcertificate/get-attachment-id
 * Body: { authCode, loanId }
 * Response: an array or object with attachment info (or the shape your backend returns)
 */
export const fetchLatestApprovalCertificateList = async (authCode, loanId) => {
  const url = `${API_DOMAIN}/api/approvalcertificate/get-attachments`;
  const response = await axios.post(url, { authCode, loanId });
  return response.data;
};

/**
 * Fetches all approval letters associated with the specified loan.
 *
 * The API returns Conditional Approval, Final Approval, and other supported
 * approval documents. The results are expected to be sorted by created date
 * in descending order by the backend.
 *
 * @param {string} authCode - Encompass authorization code used by the API to obtain an access token.
 * @param {string} loanId - Unique Encompass loan identifier.
 * @returns {Promise<Array>} A list of approval letter attachments.
 * @throws {Error} Throws when the API request fails.
 */
export const fetchAllApprovalLetters = async (authCode, loanId) => {
    const url = `${API_DOMAIN}/api/approvalcertificate/get-all-approval-letters`;

    const response = await axios.post(url, {
        authCode,
        loanId
    });

    return response.data;
};

/**
 * Downloads the specified Approval Certificate PDF file for the given loan and attachment ID.
 *
 * @param {string} authCode - The authorization code from Encompass guest session.
 * @param {string} loanId - The Encompass loan ID.
 * @param {string} attachmentId - The specific attachment ID to download.
 * @param {string} attachmentType - Either "conditional" or "final".
 * @returns {Promise<Blob>} - The downloaded PDF file as a Blob object.
 */
export const downloadApprovalCertificate = async (authCode, loanId, attachmentId, attachmentType) => {
    const url = `${API_DOMAIN}/api/approvalcertificate/get-download-url`;
    const response = await axios.post(
        url,
        { authCode, loanId, attachmentId, attachmentType },
        { responseType: 'blob' } // Important to get PDF as binary
    );
    return response.data;
};


/**
 * Get the latest Compliance Report attachment ID
 * @param {string} accessToken
 * @param {string} loanId
 * @returns {Promise<string|null>} latest Compliance Report attachment ID
 */
export const getComplianceReportAttachmentId = async (accessToken, loanId) => {
  const url = `/encompass/v3/loans/${loanId}/attachments?includeRemoved=false`;

  try {
    const response = await elliemaeApi.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200 && Array.isArray(response.data)) {
      // Filter only Compliance report attachments
      const reports = response.data.filter(
        (a) =>
          a.title?.toLowerCase().includes("compliance report") && a.isActive
      );

      if (reports.length === 0) return null;

      // Get the most recent one based on createdDate
      reports.sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
      );

      return reports[0].id;
    }

    return null;
  } catch (error) {
    console.error("Error fetching compliance report attachment:", error);
    return null;
  }
};

/**
 * Get temporary download URL for a Compliance Report attachment
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} attachmentId
 * @returns {Promise<{url: string, authorizationHeader: string, contentType: string}>}
 */
export const getComplianceReportDownloadUrl = async (
  accessToken,
  loanId,
  attachmentId
) => {
  const url = `/encompass/v3/loans/${loanId}/attachmentDownloadUrl`;

  const body = {
    attachments: [attachmentId],
  };

  try {
    const response = await elliemaeApi.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200 && response.data.attachments?.length > 0) {
      return response.data.attachments[0];
    }

    return null;
  } catch (error) {
    console.error("Error generating Compliance Report download URL:", error);
    return null;
  }
};

/**
 * Download the Compliance Report PDF file
 * @param {string} downloadUrl
 * @param {string} authorizationHeader
 */
export const downloadComplianceReportPdf = async (downloadUrl, authorizationHeader) => {
  try {
    const response = await axios.get(downloadUrl, {
      headers: {
        Authorization: authorizationHeader,
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "Compliance_Report.pdf";
    link.click();
  } catch (error) {
    console.error("Error downloading Compliance Report:", error);
  }
};
